import type { LocalLandingResolved, LocalSeoCategoryDefinition } from './localSeoLanding';
import subcategoryMetaJson from '../../../data/seo/localSeoSubcategoryMeta.json';

interface RelatedLocalService {
  path: string;
  label: string;
}

interface SubSlugEnhancement {
  title: string;
  /** Used for Quebec landings where French queries dominate Search Console CTR. */
  titleFr?: string;
  description: string;
  descriptionFr?: string;
  h1: string;
  h1Fr?: string;
  /** Unique on-page copy. Includes the queries that earn impressions for this service. */
  intro?: string;
  introFr?: string;
  related?: RelatedLocalService[];
  relatedGuide?: string;
  extraFaqs: Array<{ question: string; answer: string }>;
}

const SUB_SLUG_ENHANCEMENTS: Readonly<Record<string, SubSlugEnhancement>> =
  subcategoryMetaJson as Record<string, SubSlugEnhancement>;

function replacePlaceholders(
  template: string,
  cityName: string,
  provinceName: string,
  subName: string,
  categoryHeading?: string,
): string {
  return template
    .replace(/\{city\}/g, cityName)
    .replace(/\{province\}/g, provinceName)
    .replace(/\{subcategory\}/g, subName)
    .replace(/\{category\}/g, categoryHeading ?? '');
}

export function isLocalSeoQuebecProvince(provinceName: string): boolean {
  const n = provinceName.trim().toLowerCase();
  return n === 'quebec' || n === 'québec';
}

/**
 * Subcategory SEO enhancements — loaded from
 * {@see resources/data/seo/localSeoSubcategoryMeta.json}
 * (must stay aligned with {@see app/Support/LocalSeo/LocalSeoLandingSeoEnhancer.php}).
 * Tuned from Google Search Console: service-first titles, the queries that earn
 * impressions, bilingual Quebec copy, and unique intro text for thin landing pages.
 */
export function getEnhancedLocalLandingMeta(
  resolved: LocalLandingResolved,
): { title: string; description: string; h1: string } | null {
  const sub = resolved.subcategory;
  if (!sub) return null;

  const config = SUB_SLUG_ENHANCEMENTS[sub.slug];
  if (!config) return null;

  const { cityName, provinceName, category } = resolved;
  const quebec = isLocalSeoQuebecProvince(provinceName);
  const titleTemplate = quebec && config.titleFr ? config.titleFr : config.title;
  const descriptionTemplate = quebec && config.descriptionFr ? config.descriptionFr : config.description;
  const h1Template = quebec && config.h1Fr ? config.h1Fr : config.h1;

  return {
    title: replacePlaceholders(titleTemplate, cityName, provinceName, sub.name, category.serviceHeading),
    description: replacePlaceholders(
      descriptionTemplate,
      cityName,
      provinceName,
      sub.name,
      category.serviceHeading,
    ),
    h1: replacePlaceholders(h1Template, cityName, provinceName, sub.name, category.serviceHeading),
  };
}

export function getLocalLandingH1(resolved: LocalLandingResolved): string {
  const enhanced = getEnhancedLocalLandingMeta(resolved);
  if (enhanced) return enhanced.h1;

  const { cityName, category, subcategory } = resolved;
  return subcategory
    ? `${subcategory.name} in ${cityName}`
    : `${category.serviceHeading} in ${cityName}`;
}

/** Body copy that names the searches this page should rank for. */
export function getLocalLandingIntro(resolved: LocalLandingResolved): string | null {
  const sub = resolved.subcategory;
  if (!sub) return null;

  const config = SUB_SLUG_ENHANCEMENTS[sub.slug];
  if (!config) return null;

  const quebec = isLocalSeoQuebecProvince(resolved.provinceName);
  const template = quebec && config.introFr ? config.introFr : config.intro;
  if (!template) return null;

  return replacePlaceholders(
    template,
    resolved.cityName,
    resolved.provinceName,
    sub.name,
    resolved.category.serviceHeading,
  );
}

export interface RelatedLocalLandingLink {
  href: string;
  label: string;
}

/** Same-city services that share search demand with this page. */
export function getRelatedLocalLandingLinks(resolved: LocalLandingResolved): RelatedLocalLandingLink[] {
  const sub = resolved.subcategory;
  if (!sub) return [];

  const related = SUB_SLUG_ENHANCEMENTS[sub.slug]?.related;
  if (!related?.length) return [];

  const { provinceCode, citySlug, cityName, provinceName } = resolved;
  const prefix = `/locations/${provinceCode.toLowerCase()}/${citySlug}`;

  return related.map((item) => ({
    href: `${prefix}/${item.path.replace(/^\//, '')}`,
    label: replacePlaceholders(item.label, cityName, provinceName, sub.name),
  }));
}

export interface LocalLandingFaqItem {
  q: string;
  a: string;
}

export function buildLocalLandingTextFaqs(resolved: LocalLandingResolved): LocalLandingFaqItem[] {
  const { cityName, provinceName, category, subcategory } = resolved;
  const sub = subcategory;

  const base: LocalLandingFaqItem[] = [
    {
      q: sub
        ? `How do I book ${sub.name} in ${cityName}?`
        : `How do I book ${category.seoServicePhrase} in ${cityName}?`,
      a: sub
        ? `Start on Virtoly: click on ${sub.name} to start your order, we walk you through some simple questions and post your order. Experts see your order and will reach out to you with competitive quotes.`
        : `Start on Virtoly: click on ${category.serviceHeading} to start your order, we walk you through some simple questions and post your order. Experts see your order and will reach out to you with competitive quotes.`,
    },
    {
      q: `Are experts on Virtoly vetted in ${provinceName}?`,
      a: 'Virtoly focuses on trusted bookings: expert profiles, reviews, and platform policies help you choose with confidence.',
    },
    {
      q: 'How does pricing work?',
      a: 'You request the job, experts respond with quotes, and you choose the offer that fits your budget before work is scheduled.',
    },
  ];

  if (!sub) return base;

  const config = SUB_SLUG_ENHANCEMENTS[sub.slug];
  if (!config?.extraFaqs.length) return base;

  const extras = config.extraFaqs.map((faq) => ({
    q: replacePlaceholders(faq.question, cityName, provinceName, sub.name, category.serviceHeading),
    a: replacePlaceholders(faq.answer, cityName, provinceName, sub.name, category.serviceHeading),
  }));

  return [...base, ...extras];
}

export function getRelatedGuideForLocalLanding(resolved: LocalLandingResolved): { slug: string; title: string } | null {
  const sub = resolved.subcategory;
  if (!sub) return null;

  const config = SUB_SLUG_ENHANCEMENTS[sub.slug];
  if (!config?.relatedGuide) return null;

  return { slug: config.relatedGuide, title: '' };
}

/** Guide slug → display title for local landing cross-links. */
export const LOCAL_LANDING_GUIDE_TITLES: Readonly<Record<string, string>> = {
  'how-to-hire-a-deck-builder-in-ontario': 'How to Hire a Deck Builder in Ontario',
  'bathroom-renovation-cost-and-how-to-hire': 'Bathroom Renovation Cost & How to Hire',
  'post-construction-cleaning-guide': 'Post-Construction Cleaning Guide',
  'pet-transportation-in-canada': 'Pet Transportation in Canada',
  'mold-inspection-when-you-need-it': 'Mold Inspection: When You Need It',
  'how-to-hire-a-cleaner-in-canada': 'How to Hire a Cleaner in Canada',
  'how-much-does-a-handyman-cost': 'How Much Does a Handyman Cost?',
  'moving-services-how-to-choose': 'Moving Services: How to Choose',
};

export function getRelatedGuideLink(resolved: LocalLandingResolved): { href: string; title: string } | null {
  const guide = getRelatedGuideForLocalLanding(resolved);
  if (!guide) return null;

  const title = LOCAL_LANDING_GUIDE_TITLES[guide.slug];
  if (!title) return null;

  return { href: `/guides/${guide.slug}`, title };
}

export function buildLocalLandingFaqMainEntity(
  resolved: LocalLandingResolved,
): Array<Record<string, unknown>> {
  return buildLocalLandingTextFaqs(resolved).map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.a,
    },
  }));
}

export function getSubSlugEnhancement(subSlug: string): SubSlugEnhancement | undefined {
  return SUB_SLUG_ENHANCEMENTS[subSlug];
}

export type { LocalSeoCategoryDefinition };
